import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';
import { NewsArticle } from '../../data/news';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import Newsletter from '../Common/Newsletter/Newsletter';
import Footer from '../Common/Footer/Footer';
import useSectionRefs from '../../hooks/useSectionRefs';
import HeaderImage from '../../assets/images/cybernews.png';
import Navbar from '../Common/Navbar/Navbar';
import CyberVideo from '../../assets/images/cybernews.mp4';
import { Button } from '../../components/ui/button';
import { Globe } from '../../components/ui/globe';
import Header from '../Common/Header/Header';
import { fetchAllArticles, fetchArticlesByCategory } from '../../services/api';
import { Skeleton } from '../../components/ui/skeleton';

const categoryColors = {
  exploits: '#3b82f6',
  Monde: '#8b5cf6',
  France: '#ef4444',
  quantique: '#10b981'
};

const CyberNewsList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const sectionRefs = useSectionRefs();  
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [allArticles, setAllArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get current locale
  const locale = i18n.language || 'fr';

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const articles = await fetchAllArticles();
        setAllArticles(articles);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch articles:', err);
        setError('Failed to load articles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const handleOpenContactModal = () => {
    setIsContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
  };
  const categories = [
    { name: 'Actualités de la semaine', label: 'semaine' as const },
    { name: 'Monde', label: 'Monde' as const },
    { name: 'France', label: 'France' as const },
    { name: 'Exploits', label: 'exploits' as const },
  ];

  return (
    <div>
      <section 
        id="header" 
        className="section dark tw-min-h-[80vh]"
        ref={sectionRefs.header}
      >
        <Navbar 
          sectionRefs={sectionRefs}
          openContactModal={handleOpenContactModal}
          closeContactModal={handleCloseContactModal}
        />
        <Header 
        title={t('cyberguerre.header.title', 'Cyberguerre')} 
        subtitle={t('cyberguerre.header.subtitle', 'S\'informer sur les enjeux cyber actuels.')} 
      />
      </section>

      <div className="tw-flex tw-mt-[4em] tw-flex-col md:tw-flex-row tw-gap-8 tw-container tw-mx-auto tw-px-4">
        <div className="tw-flex-1 tw-space-y-12 tw-mb-16">
          {loading ? (
            // Loading skeleton
            <div className="tw-space-y-12">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="tw-space-y-6">
                  <Skeleton className="tw-h-10 tw-w-64" />
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                    {[1, 2, 3, 4].map((j) => (
                      <Card key={j} className="tw-transition-shadow">
                        <CardHeader>
                          <Skeleton className="tw-h-6 tw-w-full" />
                          <Skeleton className="tw-h-4 tw-w-32 tw-mt-2" />
                        </CardHeader>
                        <div className="tw-pt-4 tw-pb-4 tw-w-[380px]">
                          <Skeleton className="tw-w-full tw-h-48 tw-rounded-lg" />
                        </div>
                        <CardContent>
                          <Skeleton className="tw-h-4 tw-w-full" />
                          <Skeleton className="tw-h-4 tw-w-full tw-mt-2" />
                          <Skeleton className="tw-h-4 tw-w-2/3 tw-mt-2" />
                          <Skeleton className="tw-h-4 tw-w-24 tw-mt-4" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="tw-text-center tw-py-10">
              <p className="tw-text-red-500">{error}</p>
              <Button
                onClick={() => window.location.reload()}
                className="tw-mt-4"
              >
                Réessayer
              </Button>
            </div>
          ) : (
            categories.map((category) => {
              const filteredArticles = allArticles
                .filter(article => article.category === category.label)
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .slice(0, 4);
  
              return (
                <section 
                  key={category.label} 
                  className="tw-space-y-6"
                >
                  <h2 
                    className="tw-text-[43px] tw-leading-[1.1] tw-m-0 tw-p-0 tw-bg-gradient-to-r tw-from-[#24bbd9] tw-to-[#4166bc] tw-bg-clip-text tw-text-transparent tw-pb[4em]"
                  >
                    {category.name}
                  </h2>
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                    {filteredArticles.map((article: NewsArticle) => (
                      <Card key={article.id} className="tw-transition-shadow">
                        <CardHeader>
                          <CardTitle className="tw-text-xl">{article.title}</CardTitle>
                          <p className="tw-text-sm tw-text-muted-foreground tw-mt-1">
                            {new Date(article.date).toLocaleDateString()}
                          </p>
                        </CardHeader>
                        <div className="tw-pt-4 tw-pb-4 tw-w-[380px]">
                          <Link to={`/${locale}/cyberguerre/${article.category}/${article.slug}`}>
                            <img
                              src={article.image}
                              alt={article.title}
                              className="tw-w-full tw-h-48 tw-object-cover tw-rounded-lg tw-mb-4 tw-transition-opacity"
                            />
                          </Link>
                        </div>
                        <CardContent>
                          <p className="tw-text-muted-foreground tw-line-clamp-3 tw-mb-4">
                            {article.summary}
                          </p>
                          <Link 
                            to={`/${locale}/cyberguerre/${article.category}/${article.slug}`} 
                            className="tw-text-primary tw-font-medium"
                          >
                            Lire la suite →
                          </Link>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
        <div>
          <div className="tw-w-full md:tw-w-80 tw-space-y-8 tw-mt-20">
            <Card>
              <CardHeader>
                <CardTitle className="tw-text-[24px] tw-leading-[1.1] tw-m-0 tw-p-0 tw-bg-gradient-to-r tw-from-[#24bbd9] tw-to-[#4166bc] tw-bg-clip-text tw-text-transparent tw-pb[4em]">Articles les plus lus</CardTitle>
              </CardHeader>
              <CardContent className="tw-space-y-4">
                {loading ? (
                  // Loading skeleton for popular articles
                  Array(4).fill(0).map((_, i) => (
                    <div key={i}>
                      <div className="tw-flex tw-items-center tw-gap-[0.45rem]">
                        <Skeleton className="tw-w-12 tw-h-12 tw-rounded" />
                        <Skeleton className="tw-h-5 tw-w-full" />
                      </div>
                      <Separator className="tw-my-3" />
                    </div>
                  ))
                ) : (
                  allArticles.slice(0, 4).map((article) => (
                    <div key={article.id}>
                      <Link 
                        to={`/${locale}/cyberguerre/${article.category}/${article.slug}`}
                        className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-[0.45rem]"
                      >
                        <img
                          src={article.image}
                          alt={article.title}
                          className="tw-w-12 tw-h-12 tw-object-cover tw-rounded"
                        />
                        {article.title}
                      </Link>
                      <Separator className="tw-my-3" />
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
            
            <div className="tw-hidden md:tw-block tw-space-y-8">
              {categories.map((cat) => {
                const latestArticles = allArticles
                  .filter(a => a.category === cat.label)
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .slice(0, 3);
                return (
                  <div key={cat.label}>
                    <section id="start" className="section light" ref={sectionRefs.start}>
                      <Card className="tw-bg-gray-50">
                        <CardHeader>
                          <CardTitle className="tw-text-lg">Derniers articles : {cat.name}</CardTitle>
                        </CardHeader>
                        <CardContent className="tw-space-y-4">
                          {loading ? (
                            // Loading skeleton for category articles
                            Array(3).fill(0).map((_, i) => (
                              <div key={i} className="tw-flex tw-gap-[0.45rem]">
                                <Skeleton className="tw-w-16 tw-h-16 tw-rounded" />
                                <div className="tw-space-y-2">
                                  <Skeleton className="tw-h-4 tw-w-32" />
                                  <Skeleton className="tw-h-3 tw-w-20" />
                                </div>
                              </div>
                            ))
                          ) : (
                            latestArticles.map(article => (
                              <div key={article.id} className="tw-flex tw-gap-[0.45rem]">
                                <Link to={`/${locale}/cyberguerre/${article.category}/${article.slug}`}>
                                  <img
                                    src={article.image}
                                    alt={article.title}
                                    className="tw-w-16 tw-h-16 tw-object-cover tw-rounded"
                                  />
                                </Link>
                                <div>
                                  <Link
                                    to={`/${locale}/cyberguerre/${article.category}/${article.slug}`}
                                    className="tw-text-sm tw-font-medium tw-line-clamp-2"
                                  >
                                    {article.title}
                                  </Link>
                                  <p className="tw-text-xs tw-text-muted-foreground tw-mt-1">
                                    {new Date(article.date).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </CardContent>
                      </Card>
                    </section>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <section id="newsletter" className="section dark" ref={sectionRefs.newsletter}>
        <Newsletter />
        <div className="tw-bg-[#1d1d1d]">
          <Footer />
        </div>
      </section>
    </div>
  );
};

export default CyberNewsList;